import React from "react";
import PropTypes from "prop-types";
import Facebook from "./Facebook";
import Twitter from "./Twitter";
import _ from "lodash";
import metaData from "./metadata.json";

export const SUPPORTED_HREFLANG_REGIONS = [
  { code: "IN", name: "India", path: "", region: "IN" },
  { code: "ME", name: "Middle East", path: "middle-east", region: "ME" },
  { code: "AE", name: "UAE", path: "middle-east", region: "ME" },
  { code: "SA", name: "Saudi Arabia", path: "middle-east", region: "ME" },
  { code: "BH", name: "Bahrain", path: "middle-east", region: "ME" },
  { code: "OM", name: "Oman", path: "middle-east", region: "ME" },
  { code: "QA", name: "Qatar", path: "middle-east", region: "ME" },
  { code: "KW", name: "Kuwait", path: "middle-east", region: "ME" },
  { code: "ID", name: "Indonesia", path: "indonesia", region: "ID" },
  { code: "SG", name: "Singapore", path: "singapore", region: "SG" },
  { code: "MY", name: "Malaysia", path: "malaysia", region: "MY" },
];

// Complete tutorial: https://www.gatsbyjs.org/docs/add-seo-component/

const SEO = ({
  title,
  desc,
  banner,
  pathname,
  article,
  node,
  hrefLang,
  hreflangRegions,
  datoSeo,
  jsonldSchema,
  availableInRegions,
  site,
}) => {
  const {
    buildTime,
    siteMetadata: {
      siteUrl,
      defaultTitle,
      defaultDescription,
      defaultBanner,
      headline,
      siteLanguage,
      ogLanguage,
      author,
      twitter,
      facebook,
    },
  } = site;

  const datePublished =
    node?.publishedAt || node?.datePublished || "2019-01-18T10:30:00+01:00";
  const dateModified = node?.updatedAt || node?.dateModified || buildTime;

  let image = datoSeo?.image?.url;
  if (!image) {
    image =
      banner && banner.startsWith("http")
        ? banner
        : `${siteUrl}${banner || defaultBanner}`;
  }

  const seo = {
    title: datoSeo?.title || title || "",
    description: datoSeo?.description || desc || "",
    image,
    url: `${siteUrl}${pathname || ""}`,
    hrefLang: hrefLang || "",
    twitterCard: datoSeo?.twitterCard || "summary_large_image",
  };

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  // You can fill out the 'author', 'creator' with more data or another type (e.g. 'Organization')

  const schemaOrgWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: seo.url,
    headline,
    inLanguage: siteLanguage,
    mainEntityOfPage: seo.url,
    description: defaultDescription,
    name: defaultTitle,
    author: {
      "@type": "Person",
      name: author,
    },
    copyrightHolder: {
      "@type": "Person",
      name: author,
    },
    copyrightYear: "2023",
    creator: {
      "@type": "Person",
      name: author,
    },
    publisher: {
      "@type": "Person",
      name: author,
    },
    datePublished,
    dateModified,
    image: {
      "@type": "ImageObject",
      url: `${siteUrl}${defaultBanner}`,
    },
  };

  // Initial breadcrumb list
  const pathSegments = pathname?.split("/").filter(Boolean) || [];

  const itemListElement = [
    {
      "@type": "ListItem",
      item: {
        "@id": siteUrl,
        name: "Homepage",
      },
      position: 1,
    },
  ];

  let currentPath = "";

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    itemListElement.push({
      "@type": "ListItem",
      item: {
        "@id": `${siteUrl}${currentPath}/`,
        name: segment
          .replace(/-/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
      },
      position: index + 2,
    });
  });
  let schemaArticle = null;

  if (article) {
    schemaArticle = {
      "@context": "https://schema.org",
      "@type": "Article",
      author: {
        "@type": "Person",
        name: author,
      },
      copyrightHolder: {
        "@type": "Person",
        name: author,
      },
      copyrightYear: "2023",
      creator: {
        "@type": "Person",
        name: author,
      },
      publisher: {
        "@type": "Organization",
        name: author,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}${defaultBanner}`,
        },
      },
      datePublished,
      dateModified,
      description: seo.description,
      headline: seo.title,
      inLanguage: siteLanguage,
      url: seo.url,
      name: seo.title,
      image: {
        "@type": "ImageObject",
        url: seo.image,
      },
      mainEntityOfPage: seo.url,
    };
  }

  let schemaHomePage = null;

  if (node && node.type === "Organization") {
    schemaHomePage = {
      "@context": "https://schema.org",
      "@type": node.type,
      name: node.name,
      url: node.url ? node.url : siteUrl,
      logo: `${siteUrl}${defaultBanner}`,
      contactPoint: node.contactPoint,
      sameAs: node.sameAs,
    };
  }

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    description: "Breadcrumbs list",
    name: "Breadcrumbs",
    itemListElement,
  };

  let schema = null;
  if (jsonldSchema) {
    schema = jsonldSchema;
  } else if (node && node.type === "Organization") {
    schema = schemaHomePage;
  } else if (article) {
    schema = schemaArticle;
  } else {
    schema = schemaOrgWebPage;
  }

  let supportedRegions = [];
  let regionDetails = {};
  let cleanPath = pathname;

  if (hrefLang) {
    if (!hreflangRegions) {
      supportedRegions = [{ lang: "x-default", path: siteUrl }];
    }

    for (const region of SUPPORTED_HREFLANG_REGIONS.filter((r) =>
      availableInRegions?.includes(r.region),
    )) {
      if (
        region["code"] === "IN" &&
        (!hreflangRegions || hreflangRegions.includes("IN"))
      ) {
        regionDetails = { lang: "en-in", path: siteUrl };
        supportedRegions.push(regionDetails);
      } else if (!hreflangRegions || hreflangRegions.includes(region["code"])) {
        let regionCode = `/${region["path"]}`;
        regionDetails = {
          lang: `en-${_.toLower(region["code"])}`,
          path: `${siteUrl}${regionCode}`,
        };
        if (pathname.includes(regionCode)) {
          cleanPath = pathname.split(regionCode);
          cleanPath = cleanPath[cleanPath.length - 1];
        }
        supportedRegions.push(regionDetails);
      }
    }
  }

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <link rel="canonical" href={seo.url} />
      {seo.hrefLang
        ? supportedRegions.map((region, i) => (
            <link
              rel="alternate"
              hrefLang={region["lang"]}
              href={`${region["path"]}${cleanPath}`}
              key={i}
            />
          ))
        : null}
      {/* Insert schema.org data conditionally (webpage/article) + everytime (breadcrumbs) */}
      {/* Handling Multiple Schema*/}
      {Array.isArray(schema) ? (
        schema.map((item, index) => (
          <script key={`schema-${index}`} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      {/* this takes data from methadata.json which is created while build time and adds meta tags to header */}
      {metaData
        ? Object.keys(metaData).map((key, index) => (
            <meta name={key} content={metaData[key]} key={index} />
          ))
        : null}
      <Facebook
        desc={seo.description}
        image={seo.image}
        title={seo.title}
        type={article ? "article" : "website"}
        url={seo.url}
        locale={ogLanguage}
        name={facebook}
      />
      <Twitter
        type={seo.twitterCard}
        title={seo.title}
        image={seo.image}
        desc={seo.description}
        username={twitter}
      />
    </>
  );
};

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  desc: PropTypes.string,
  banner: PropTypes.string,
  pathname: PropTypes.string,
  article: PropTypes.bool,
  node: PropTypes.object,
  site: PropTypes.object.isRequired,
};

SEO.defaultProps = {
  title: null,
  desc: null,
  banner: null,
  pathname: null,
  article: false,
  node: null,
};
